import { cn } from "@/lib/utils";

// The shape MUI's `Chip` has, as class strings.
//
// The Timmy theme adds **no `MuiChip` override** — the only `MuiChip-*` selectors
// in the whole donor are per-instance `sx` on screens this migration has not
// reached — so `@mui/material/Chip/Chip.js` (v9.0.1) is the parity spec verbatim,
// and every value below is cited to it:
//
//   - 32px tall, fully rounded (16px radius), a 13px label, `text.primary` ink,
//   - filled on `action.selected`, or transparent behind a 1px `grey[400]` line,
//   - 12px of label padding filled, 11px outlined — the border eats the pixel,
//   - at a palette colour, an outlined chip's *ink* moves with its border,
//   - clickable, two ground tints: `action.hover`, then `action.focus` on
//     keyboard focus.
//
// Class strings beside `chip.tsx` rather than inside it — the same split as
// `mui-field-shape.ts`, and for the same reason. Two callers need the same
// numbers at two different markups: a variant chip is a `<button>` around a text
// label, while a removable filter chip (`chip-list.tsx`) is a `<span>` wrapping a
// label *and* a delete control, padded asymmetrically. Forcing the second through
// the first would mean a slot prop and a padding prop with one caller each.
// Sharing the numbers is the point; sharing the markup is not.

/**
 * MUI's chip root, minus the colour rules and minus anything about being
 * clickable: `Chip.js:74-99`.
 *
 * No horizontal padding — that lives on the label, and the two variants and the
 * removable row all pad differently, so every caller states its own. Leaving it
 * out is also what keeps callers clear of the Tailwind cascade hazard documented
 * in `item-image-box.tsx`: at equal specificity the later-*emitted* rule wins, so
 * a `pr-1.5` meant to override a shared `px-3` would be settled by Tailwind's
 * ordering rather than by the class string.
 */
export const CHIP_ROOT = cn(
  "inline-flex h-8 max-w-full items-center justify-center rounded-2xl align-middle text-[0.8125rem] leading-normal whitespace-nowrap",
  // `outline: 0` — MUI shows focus as a *ground tint* rather than a ring (the
  // `focusVisible` rules below), so removing the ring is the port, not a lapse.
  "outline-none",
  // `getTransitionStyles(theme, ['background-color', 'box-shadow'])`, which is
  // `transitions.create` at its defaults: 300ms `standard`, `easeInOut` —
  // Tailwind v4's own `--ease-in-out` is that same cubic-bezier. MUI drops the
  // transition under `prefers-reduced-motion`, hence the last utility.
  "transition-[background-color,box-shadow] duration-300 ease-in-out motion-reduce:transition-none",
);

/**
 * `size: 'small'` over `CHIP_ROOT`: 24px where the medium chip is 32
 * (`Chip.mjs`, `sizeSmall`). The radius stays 16px — MUI keeps it at both
 * sizes, which at 24px still reads as fully rounded.
 */
export const CHIP_ROOT_SMALL = "h-6";

/**
 * `.MuiChip-iconSmall`: an 18px glyph, 4px in from the chip's left edge and
 * pulled 4px back into the label's padding (`Chip.mjs`). The medium icon is
 * `5px / -6px` at the glyph's own size.
 */
export const CHIP_ICON_SMALL = "ml-1 -mr-1 size-[18px] shrink-0";
export const CHIP_ICON = "ml-[5px] -mr-1.5 shrink-0";

/**
 * The outlined variant's icon inset (`Chip.js:262-282`): the 1px border stands
 * where the margin's first pixel was, so MUI moves the icon in by less — 4px
 * at medium, 2px at small. Laid over the size's own slot class; the pull into
 * the label is unchanged.
 */
export const CHIP_ICON_OUTLINED = "ml-1";
export const CHIP_ICON_SMALL_OUTLINED = "ml-0.5";

/** The label's own rules (`Chip.js:312-321`), minus its padding. The ellipsis is
 * why a clamped chip truncates instead of clipping. */
export const CHIP_LABEL = "overflow-hidden text-ellipsis";

/** `variant: 'filled'` at the default colour — `action.selected` ground,
 * `text.primary` ink (`Chip.js:82-84`). The label pads 12px a side. */
export const CHIP_FILLED = "bg-lennar-action-selected text-lennar-ink";

/** `variant: 'outlined'` at the default colour: `Chip.js:249-268`. The label pads
 * 11px a side. */
export const CHIP_OUTLINED = "border-lennar-grey-400 text-lennar-ink border bg-transparent";

/**
 * `variant: 'outlined'` at a palette colour: `Chip.js:288-306`.
 *
 * **The ink moves with the border.** An outlined primary chip is
 * `palette.primary.main` text inside a border of that same colour at 0.7 — not
 * default ink in a blue box. Missing that is what makes a ported "selected" chip
 * read as merely outlined.
 */
export const CHIP_OUTLINED_PRIMARY =
  "border-lennar-primary-border text-lennar-blue-600 border bg-transparent";

/**
 * "Where to save"'s share chip (#1217): outlined at the default colour, then
 * the donor's `sx` — `color` and `borderColor` `#940d0f`, `fontWeight: 'bold'`
 * (`cost-saving-summary-table.tsx`, `PriceChip`). Its clickable grounds stay
 * the default's, which the `sx` leaves alone.
 */
export const CHIP_OUTLINED_OVER_TARGET =
  "border-target-cost-over text-target-cost-over border bg-transparent font-bold";

/**
 * The ring MUI shows while a clickable chip is held down.
 *
 * `Chip.js`'s `clickable` variant declares `'&:active': { boxShadow:
 * theme.shadows[1] }`, and the `outlined` variant blocks that follow redefine
 * background, border, hover and focusVisible but never `:active` — so it
 * survives on an outlined chip. The Timmy theme replaces `shadows[1]` with
 * `'0px 0px 0px 1px #e0e0e0'` (`react-ui/src/theme/theme.ts:395-399`), i.e. a
 * flat 1px `grey.300` ring rather than MUI's own drop shadow.
 *
 * Written as the literal rather than through the `--color-lennar-grey-300`
 * token because a `shadow-[…]` arbitrary value takes a colour, not a utility,
 * and the two spellings would then have to be kept in step by hand.
 */
const CHIP_ACTIVE_RING = "active:shadow-[0px_0px_0px_1px_#e0e0e0]";

/** What `ButtonBase` adds on top (`ButtonBase.js:52-64`), and the two grounds MUI
 * tints a clickable chip with: `action.hover`, then `action.focus` on keyboard
 * focus — which is what `focus-visible:` is. */
export const CHIP_CLICKABLE = cn(
  // `relative` for the ripple layer, which is `absolute inset-0`, and no
  // `overflow-hidden` on the chip itself: the layer clips itself
  // (`rounded-[inherit]`), and `overflow: hidden` on a `rounded-full` chip
  // would clip its label's hit area along with its paint.
  "relative",
  "cursor-pointer select-none",
  "hover:bg-lennar-action-hover focus-visible:bg-lennar-action-focus",
  CHIP_ACTIVE_RING,
);

/** The same two grounds at `alpha(primary.main, …)` (`Chip.js:297-301`). */
export const CHIP_CLICKABLE_PRIMARY = cn(
  "relative",
  "cursor-pointer select-none",
  "hover:bg-lennar-primary-hover focus-visible:bg-lennar-primary-focus",
  CHIP_ACTIVE_RING,
);

/**
 * `variant: 'filled'` at a palette colour — `palette[color].main` under its
 * `contrastText` (`Chip.js:167-176`). The one caller is the Core
 * `SpecificationChip`, at the theme's `chipColor` (`#1f75bb` under white,
 * `react-ui/src/theme/theme.ts:268-271`).
 */
export const CHIP_FILLED_SPEC = "bg-lennar-blue-400 text-white";

/**
 * `variant: 'outlined'` at the same two custom colours, by the rule
 * `CHIP_OUTLINED_PRIMARY` carries for `primary`: the ink is the colour, the
 * border 0.7 of it (`Chip.js:289-296`). `chipColor` for Base, and
 * `secondaryChipColor` (`#455a64`, `theme.ts:272-275`) for the edit header's
 * grey "Draft".
 */
export const CHIP_OUTLINED_SPEC =
  "border-spec-chip-border text-lennar-blue-400 border bg-transparent";
export const CHIP_OUTLINED_SECONDARY =
  "border-spec-secondary-chip-border text-spec-secondary-chip border bg-transparent";
